import { IconProps } from 'grommet-icons';
import { TReportKey } from '/src/api';
import { EquipmentIcon, FacilityIcon, StatementIcon, UserIcon } from '/src/components';

export type ReportFilterDefinition = {
  key: string;
  labelKey: string;
  type: 'multiple' | 'enum';
  component?: 'clients' | 'programs';
  enumOptions?: { value: string; labelKey: string }[];
  defaultValue?: string;
};

export type ReportDefinition = {
  key: TReportKey;
  slug: string;
  titleKey: string;
  descriptionKey: string;
  icon: React.FC<IconProps>;
  columns: string[];
  adminOnlyColumns?: string[];
  filters: ReportFilterDefinition[];
};

const statusEnumOptions = [
  { value: 'all', labelKey: 'reports:optionAll' },
  { value: 'active', labelKey: 'reports:optionActive' },
  { value: 'inactive', labelKey: 'reports:optionInactive' },
];

const meteredEnumOptions = [
  { value: 'all', labelKey: 'reports:optionAll' },
  { value: 'yes', labelKey: 'reports:optionYes' },
  { value: 'no', labelKey: 'reports:optionNo' },
];

const clientNameFilter: ReportFilterDefinition = {
  key: 'client_ids',
  labelKey: 'reports:filterClientName',
  type: 'multiple',
  component: 'clients',
};

const reportingPeriodFilter: ReportFilterDefinition = {
  key: 'reporting_periods',
  labelKey: 'reports:filterReportingPeriod',
  type: 'multiple',
};

const programFilter: ReportFilterDefinition = {
  key: 'program_ids',
  labelKey: 'reports:filterProgram',
  type: 'multiple',
  component: 'programs',
};

const cityFilter: ReportFilterDefinition = {
  key: 'cities',
  labelKey: 'reports:filterCity',
  type: 'multiple',
};

const stateProvinceFilter: ReportFilterDefinition = {
  key: 'region_ids',
  labelKey: 'reports:filterStateProvince',
  type: 'multiple',
};

const equipmentCategoryFilter: ReportFilterDefinition = {
  key: 'equipment_category_ids',
  labelKey: 'reports:filterEquipmentCategory',
  type: 'multiple',
};

const statusFilter: ReportFilterDefinition = {
  key: 'status',
  labelKey: 'reports:filterStatus',
  type: 'enum',
  enumOptions: statusEnumOptions,
  defaultValue: 'active',
};

export const reportDefinitions: ReportDefinition[] = [
  {
    key: 'incentive',
    slug: 'incentive-report',
    titleKey: 'reports:incentiveReport',
    descriptionKey: 'reports:incentiveReportDescription',
    icon: StatementIcon,
    columns: [
      'Client Name',
      'Reporting Period',
      'Program',
      'Facility Street',
      'Facility City',
      'Facility State / Province',
      'Facility Postal Code',
      'Equipment Category',
      'Serial Number',
      'kWh Reported',
      'Credits Generated',
      'Credit Price',
      'Total Value',
      'REC Price',
      'REC Cost',
      'Broker Fee',
      'FuSE Commission',
      'Taxes on Credit Sale',
      'Taxes on Services',
      'Net Incentive',
    ],
    filters: [
      clientNameFilter,
      reportingPeriodFilter,
      programFilter,
      cityFilter,
      stateProvinceFilter,
      equipmentCategoryFilter,
    ],
  },
  {
    key: 'equipment',
    slug: 'equipment-report',
    titleKey: 'reports:equipmentReport',
    descriptionKey: 'reports:equipmentReportDescription',
    icon: EquipmentIcon,
    columns: [
      'Client Name',
      'Reporting Period',
      'Program',
      'Facility Street Address',
      'Facility City',
      'Facility State/Province',
      'Facility Postal Code',
      'Equipment Category',
      'Equipment Type',
      'Serial Number',
      'FSE Registration Status',
      'FSE ID',
      'FSE RU',
      'Unit Number',
      'Manufacturer',
      'Model Number',
      'Model Year',
      'Comments',
      'Metered',
      'CNO/CMS',
      'Equipment Use Case',
      'Charging Type',
      'First Reporting Period',
      'First Day in Service',
      'Fuel Pathway Code',
      'Book & Claim',
      'Latitude',
      'Longitude',
      'kWh Reported',
      'Credits',
    ],
    adminOnlyColumns: ['FSE ID', 'FSE RU'],
    filters: [
      clientNameFilter,
      reportingPeriodFilter,
      statusFilter,
      programFilter,
      { key: 'facility_ids', labelKey: 'reports:filterFacilityAddress', type: 'multiple' },
      equipmentCategoryFilter,
      { key: 'equipment_type_ids', labelKey: 'reports:filterEquipmentType', type: 'multiple' },
      { key: 'manufacturers', labelKey: 'reports:filterManufacturer', type: 'multiple' },
      {
        key: 'metered',
        labelKey: 'reports:filterMetered',
        type: 'enum',
        enumOptions: meteredEnumOptions,
        defaultValue: 'yes',
      },
    ],
  },
  {
    key: 'facility',
    slug: 'facility-report',
    titleKey: 'reports:facilityReport',
    descriptionKey: 'reports:facilityReportDescription',
    icon: FacilityIcon,
    columns: [
      'Client Name',
      'Status',
      'First Reporting Period',
      'Final Reporting Period',
      'Facility Name',
      'Address',
      'Address (Continued)',
      'City',
      'State / Province',
      'Postal Code',
      'Program',
      'FSE Utility',
      'Comments',
      'Contact Name',
      'Contact Role',
      'Contact Email',
      'Contact Phone',
    ],
    filters: [
      clientNameFilter,
      statusFilter,
      programFilter,
      cityFilter,
      stateProvinceFilter,
      { key: 'utility_ids', labelKey: 'reports:filterFseUtility', type: 'multiple' },
      { key: 'contact_user_ids', labelKey: 'reports:filterContactName', type: 'multiple' },
    ],
  },
  {
    key: 'user',
    slug: 'user-report',
    titleKey: 'reports:userReport',
    descriptionKey: 'reports:userReportDescription',
    icon: UserIcon,
    columns: [
      'Client Name',
      'User Name',
      'User Email',
      'User Phone',
      'User Role',
      'Last Login',
      'Status',
      'Date Created',
    ],
    filters: [
      statusFilter,
      clientNameFilter,
      { key: 'role_ids', labelKey: 'reports:filterUserRole', type: 'multiple' },
    ],
  },
];

export const getReportDefinition = (slug?: string): ReportDefinition | undefined =>
  reportDefinitions.find((definition) => definition.slug === slug);
